'use client';

import {
  CUSTOMER_CREDIT_TYPES,
  getCustomerCreditBalance,
} from '@eliadmin/core/src/api/customers/customer-credit-utils';

import React from 'react';

import { setLoyaltyProgramPoints } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function CartApplyLoyaltyPoints() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const loyaltyProgram = useGlobalStore(s => s.generalLayout.loyalty_program);

  const { data: cart } = useCart();
  const me = useCustomerStore(s => s.me);
  const myLoyaltyPoints = getCustomerCreditBalance(
    me?.customer,
    CUSTOMER_CREDIT_TYPES.LOYALTY_POINTS,
  );

  const [points, setPoints] = React.useState(
    cart?.loyalty_program_points ? String(cart.loyalty_program_points) : '',
  );

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const pointValue = Number(loyaltyProgram?.point_to_currency ?? 0);
  const maxPointsByOrderValue =
    pointValue > 0 ? Math.floor(Number(cart?.total ?? 0) / pointValue) : Number(myLoyaltyPoints);
  const maxApplicablePoints = Math.max(0, Math.min(Number(myLoyaltyPoints), maxPointsByOrderValue));

  const applyPointsMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: setLoyaltyProgramPoints,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART] });
    },
    onError: error => {
      if (error instanceof ValidationError && error.fields.points) {
        toast({
          title: t('text594'),
          description: error.fields.points.join(' '),
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: t('text594'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    },
  });

  const handleApply = () => {
    if (!me?.authToken) return;
    const parsed = Number(points);
    if (isNaN(parsed)) return;
    const capped = Math.max(0, Math.min(parsed, maxApplicablePoints));
    applyPointsMutation.mutate(
      { locale, points: capped, authToken: me.authToken },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: t('text572'), description: t('text3507') });
        },
      },
    );
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="text-sm text-muted-foreground" suppressHydrationWarning>
        {t('text3774')}:{' '}
        <strong className="font-semibold text-foreground">
          {Intl.NumberFormat(undefined, { compactDisplay: 'long' }).format(myLoyaltyPoints)}
        </strong>
        &nbsp;
        {t('text3773')}
      </p>
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          aria-label={t('text1164')}
          placeholder={t('text3734')}
          value={points}
          type="number"
          max={maxApplicablePoints}
          onChange={e => setPoints(e.target.value)}
          onBlur={() => {
            if (points === '') return;
            const parsed = Number(points);
            if (isNaN(parsed)) {
              setPoints('');
              return;
            }
            const capped = Math.max(0, Math.min(parsed, maxApplicablePoints));
            if (capped !== parsed) setPoints(String(capped));
          }}
          className={{ wrapper: 'flex-1' }}
        />
        <Button
          disabled={!points || applyPointsMutation.isPending}
          isLoading={applyPointsMutation.isPending}
          onClick={handleApply}
          className="w-full whitespace-nowrap sm:w-fit"
        >
          {t('text3733')}
        </Button>
      </div>
    </div>
  );
}
